import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Package, RefreshCcw, Wrench, ChevronRight, CheckCircle2 } from "lucide-react";

const ORDER_STATUS_COLOR = {
  placed: "vm-chip-primary",
  packed: "vm-chip-primary",
  out_for_delivery: "vm-chip-accent",
  delivered: "vm-chip-success",
  cancelled: "vm-chip-accent",
};

export default function Profile() {
  const { user } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [buybacks, setBuybacks] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/buyback/requests").then(({ data }) => setBuybacks(data)).catch(() => {});
    api.get("/services/bookings").then(({ data }) => setServices(data)).catch(() => {});
  }, [user]);

  if (!user) return <div className="max-w-3xl mx-auto px-5 py-20 text-center text-neutral-400">Sign in to view your profile.</div>;

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10" data-testid="profile-page">
      <div className="flex items-center gap-4">
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="h-14 w-14 rounded-full" />
        ) : (
          <div className="h-14 w-14 rounded-full bg-volt-500 flex items-center justify-center font-bold text-xl">
            {user.name?.[0]?.toUpperCase()}
          </div>
        )}
        <div>
          <p className="vm-overline">My account</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1">{user.name}</h1>
          <p className="text-sm text-neutral-400">{user.email}</p>
          {user.phone && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-volt-400 font-semibold" data-testid="profile-phone-linked">
              <CheckCircle2 className="h-3.5 w-3.5" /> Phone: {user.phone}
            </p>
          )}
        </div>
      </div>

      <div className="flex gap-2 mt-8 border-b border-white/10">
        {[
          { id: "orders", label: "Orders", icon: Package, count: orders.length },
          { id: "buybacks", label: "Buybacks", icon: RefreshCcw, count: buybacks.length },
          { id: "services", label: "Services", icon: Wrench, count: services.length },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 flex items-center gap-2 transition-colors ${
              tab === t.id ? "border-volt-500 text-white" : "border-transparent text-neutral-500 hover:text-white"
            }`}
            data-testid={`profile-tab-${t.id}`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
            <span className="text-xs text-neutral-500">({t.count})</span>
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {tab === "orders" && (
          orders.length === 0 ? (
            <Empty title="No orders yet" link="/shop" cta="Start shopping" />
          ) : orders.map((o) => (
            <Link key={o.order_id} to={`/orders/${o.order_id}`} className="vm-card p-5 flex items-center gap-4" data-testid={`order-row-${o.order_id}`}>
              <div className="flex -space-x-3">
                {o.items.slice(0, 3).map((it, idx) => (
                  <img key={idx} src={it.image} alt="" className="h-12 w-12 rounded-lg border-2 border-ink-900 object-cover" />
                ))}
              </div>
              <div className="flex-1">
                <p className="text-xs font-mono text-neutral-500">{o.order_id}</p>
                <p className="text-sm font-semibold">{o.items.length} item{o.items.length > 1 ? "s" : ""} • {inr(o.total)}</p>
                <p className="text-xs text-neutral-400 mt-0.5">{new Date(o.created_at).toLocaleString()}</p>
              </div>
              <span className={`vm-chip ${ORDER_STATUS_COLOR[o.status] || ""}`}>{o.status.replace(/_/g, " ")}</span>
              <ChevronRight className="h-5 w-5 text-neutral-500" />
            </Link>
          ))
        )}

        {tab === "buybacks" && (
          buybacks.length === 0 ? (
            <Empty title="No buybacks yet" link="/buyback" cta="Sell a device" />
          ) : buybacks.map((b) => (
            <div key={b.request_id} className="vm-card p-5 flex items-center gap-4" data-testid={`buyback-row-${b.request_id}`}>
              <div className="h-12 w-12 rounded-lg bg-blaze-500/20 flex items-center justify-center"><RefreshCcw className="h-5 w-5 text-blaze-400" /></div>
              <div className="flex-1">
                <p className="text-sm font-semibold">{b.brand} {b.model}</p>
                <p className="text-xs text-neutral-400 mt-0.5">Pickup: {new Date(b.pickup_slot).toLocaleString()}</p>
              </div>
              <p className="font-display font-bold text-lg">{inr(b.quoted_price)}</p>
              <span className="vm-chip">{b.status}</span>
            </div>
          ))
        )}

        {tab === "services" && (
          services.length === 0 ? (
            <Empty title="No service bookings" link="/services" cta="Book a service" />
          ) : services.map((s) => (
            <div key={s.booking_id} className="vm-card p-5 flex items-center gap-4" data-testid={`service-row-${s.booking_id}`}>
              <div className="h-12 w-12 rounded-lg bg-volt-500/20 flex items-center justify-center"><Wrench className="h-5 w-5 text-volt-400" /></div>
              <div className="flex-1">
                <p className="text-sm font-semibold capitalize">{s.service_type} • {s.brand} {s.model}</p>
                <p className="text-xs text-neutral-400 mt-0.5">Slot: {new Date(s.slot).toLocaleString()}</p>
              </div>
              <p className="font-display font-bold">{inr(s.price_estimate)}</p>
              <span className="vm-chip">{s.status}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Empty({ title, link, cta }) {
  return (
    <div className="vm-card p-12 text-center">
      <p className="font-display text-xl font-bold">{title}</p>
      <Link to={link} className="vm-btn-primary inline-block mt-4">{cta}</Link>
    </div>
  );
}
